(() => {
  if (window.TeamsDemoModal) {
    window.TeamsDemoModal.refresh();
    return;
  }

  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const storageKey = 'groov.teams.utm.v1';
  const triggerSelector = '[data-teams-demo-trigger]';
  const focusableSelector = 'a[href], button:not([disabled]), iframe, [tabindex="0"]';
  const scheduledEvents = new Set();
  const mounts = new WeakMap();
  let widgetPromise;
  let activeModal;
  let activeTrigger;
  let lockedScrollY = 0;
  let savedBodyStyles;
  let inlineObserver;
  let headerObserver;

  const config = () => {
    try {
      const value = JSON.parse(document.querySelector('[data-teams-booking-config]')?.textContent || '{}');
      const url = new URL(value.url);
      if (url.protocol !== 'https:' || !['calendly.com', 'www.calendly.com'].includes(url.hostname) || url.pathname === '/') return null;
      // Calendly redirects www links to this origin; use it for the iframe and message check.
      url.hostname = 'calendly.com';
      return { ...value, url, measurementId: (value.measurementId || '').trim() };
    } catch { return null; }
  };

  const analyticsAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return typeof privacy?.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed();
  };

  const cleanUtms = (values) => Object.fromEntries(utmKeys
    .filter((key) => typeof values[key] === 'string' && values[key].trim())
    .map((key) => [key, values[key].trim().slice(0, 254)]));

  const utms = () => {
    if (!analyticsAllowed()) return {};
    const current = cleanUtms(Object.fromEntries(new URLSearchParams(window.location.search)));
    try {
      if (Object.keys(current).length) {
        window.sessionStorage.setItem(storageKey, JSON.stringify(current));
        return current;
      }
      return cleanUtms(JSON.parse(window.sessionStorage.getItem(storageKey) || '{}'));
    } catch { return current; }
  };

  const bookingUrl = () => {
    const current = config();
    if (!current) return '#book-a-demo';
    const url = new URL(current.url);
    utmKeys.forEach((key) => url.searchParams.delete(key));
    Object.entries(utms()).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.href;
  };

  const track = (event, section) => {
    const current = config();
    if (!current || !analyticsAllowed() || current.analyticsMode === 'disabled') return;
    const params = { section };
    if (current.analyticsMode === 'gtm') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...params });
    } else if (current.analyticsMode === 'gtag' && /^G-[A-Z0-9]+$/.test(current.measurementId)) {
      window.dataLayer = window.dataLayer || [];
      const tag = window.gtag || function () { window.dataLayer.push(arguments); };
      tag('event', event, { ...params, send_to: current.measurementId });
    }
  };

  const loadWidget = () => {
    if (window.Calendly?.initInlineWidget) return Promise.resolve();
    if (widgetPromise) return widgetPromise;
    widgetPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      const timer = window.setTimeout(() => reject(new Error('Calendar load timeout')), 15000);
      script.onload = () => {
        window.clearTimeout(timer);
        if (window.Calendly?.initInlineWidget) resolve();
        else reject(new Error('Calendar unavailable'));
      };
      script.onerror = () => {
        window.clearTimeout(timer);
        script.remove();
        reject(new Error('Calendar unavailable'));
      };
      document.head.appendChild(script);
    }).catch((error) => { widgetPromise = undefined; throw error; });
    return widgetPromise;
  };

  const showError = (host) => {
    host.removeAttribute('data-loading');
    const status = host.querySelector('[data-teams-calendar-status]');
    if (status) {
      status.textContent = 'The calendar couldn’t load. Please open it in a new tab.';
      status.hidden = false;
    }
    const fallback = host.querySelector('[data-teams-calendar-fallback]');
    if (fallback) fallback.hidden = false;
  };

  const resetCalendar = (host) => {
    mounts.delete(host);
    host.querySelector('.teams-calendar-mount')?.remove();
    host.removeAttribute('data-loading');
    host.removeAttribute('data-ready');
  };

  const mountCalendar = async (host) => {
    if (!host || !config() || host.hasAttribute('data-loading') || host.hasAttribute('data-ready')) return;
    resetCalendar(host);
    const token = {};
    mounts.set(host, token);
    const isCurrent = () => host.isConnected && mounts.get(host) === token;
    host.setAttribute('data-loading', '');
    const status = host.querySelector('[data-teams-calendar-status]');
    if (status) { status.hidden = false; status.textContent = 'Loading calendar…'; }
    try {
      await loadWidget();
      if (!isCurrent() || !config()) return;
      const mount = document.createElement('div');
      mount.className = 'teams-calendar-mount';
      host.appendChild(mount);
      window.Calendly.initInlineWidget({ url: bookingUrl(), parentElement: mount, resize: false });
      const frame = mount.querySelector('iframe');
      if (!frame) throw new Error('Calendar unavailable');
      frame.title = `Book a ${config()?.durationMinutes === 30 ? 30 : 15}-minute demo with Groov`;
      const timer = window.setTimeout(() => { if (isCurrent()) showError(host); }, 20000);
      frame.addEventListener('load', () => {
        window.clearTimeout(timer);
        if (!isCurrent()) return;
        host.removeAttribute('data-loading');
        host.setAttribute('data-ready', '');
        if (status) status.hidden = true;
        const fallback = host.querySelector('[data-teams-calendar-fallback]');
        if (fallback) fallback.hidden = true;
      }, { once: true });
    } catch { if (isCurrent()) showError(host); }
  };

  const lockPage = () => {
    lockedScrollY = window.scrollY;
    savedBodyStyles = {};
    ['position', 'top', 'width', 'overflow', 'paddingRight'].forEach((key) => { savedBodyStyles[key] = document.body.style[key]; });
    const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    Object.assign(document.body.style, { position: 'fixed', top: `-${lockedScrollY}px`, width: '100%', overflow: 'hidden' });
    if (gap) document.body.style.paddingRight = `${gap}px`;
  };

  const closeModal = () => {
    if (!activeModal) return;
    activeModal.classList.remove('is-open');
    activeModal.hidden = true;
    activeModal.setAttribute('aria-hidden', 'true');
    Object.assign(document.body.style, savedBodyStyles || {});
    window.scrollTo(0, lockedScrollY);
    const trigger = activeTrigger;
    activeModal = null;
    activeTrigger = null;
    trigger?.focus({ preventScroll: true });
  };

  const openModal = (trigger) => {
    if (activeModal) return;
    const modal = document.querySelector('[data-teams-demo-controller]');
    if (!modal || !config()) return;
    activeModal = modal;
    activeTrigger = trigger;
    const host = modal.querySelector('[data-teams-calendar]');
    host.dataset.demoSection = trigger.dataset.demoSection || 'unknown';
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    lockPage();
    mountCalendar(host);
    window.requestAnimationFrame(() => {
      if (activeModal !== modal) return;
      modal.classList.add('is-open');
      modal.querySelector('.teams-demo-modal__close')?.focus();
    });
  };

  const refresh = () => {
    const url = bookingUrl();
    const modal = document.querySelector('[data-teams-demo-controller]');
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      trigger.href = url;
      if (config() && modal) {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', modal.id);
      } else {
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-controls');
      }
    });
    document.querySelectorAll('[data-teams-calendar-fallback]').forEach((link) => { link.href = url; });
    headerObserver?.disconnect();
    const header = document.querySelector('[data-teams-header]');
    const stickyHeader = document.querySelector('[data-teams-sticky-header]');
    if (header && stickyHeader && 'IntersectionObserver' in window) {
      headerObserver = new IntersectionObserver(([entry]) => {
        stickyHeader.hidden = entry.isIntersecting;
      }, { threshold: 0 });
      headerObserver.observe(header);
    }
    inlineObserver?.disconnect();
    const inline = document.querySelector('.teams-booking__calendar');
    if (inline && config()) {
      if ('IntersectionObserver' in window) {
        inlineObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { mountCalendar(inline); inlineObserver.disconnect(); }
        }, { rootMargin: '400px' });
        inlineObserver.observe(inline);
      } else { mountCalendar(inline); }
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(triggerSelector);
    if (trigger) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      track('demo_cta_click', trigger.dataset.demoSection || 'unknown');
      if (config()) { event.preventDefault(); openModal(trigger); }
    } else if (activeModal && event.target.closest?.('[data-teams-demo-close]')) {
      event.preventDefault(); closeModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal) return;
    if (event.key === 'Escape') { event.preventDefault(); closeModal(); }
    else if (event.key === 'Tab') {
      const focusable = Array.from(activeModal.querySelectorAll(focusableSelector)).filter((el) => el.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  });
  document.addEventListener('focusin', (event) => {
    if (activeModal && !activeModal.contains(event.target)) activeModal.querySelector('.teams-demo-modal__close')?.focus();
  });

  window.addEventListener('message', (event) => {
    const current = config();
    if (!current || event.origin !== current.url.origin || event.data?.event !== 'calendly.event_scheduled') return;
    const frame = Array.from(document.querySelectorAll('[data-teams-calendar] iframe')).find((item) => item.contentWindow === event.source);
    if (!frame) return;
    const key = event.data.payload?.invitee?.uri || event.data.payload?.event?.uri;
    if (typeof key !== 'string' || !key || scheduledEvents.has(key)) return;
    scheduledEvents.add(key);
    // No invitee details, event URIs or raw Calendly payloads go to analytics.
    track('demo_booked', frame.closest('[data-teams-calendar]').dataset.demoSection || 'booking');
  });

  const updateConsent = () => {
    if (!analyticsAllowed()) {
      try { window.sessionStorage.removeItem(storageKey); } catch { /* Storage can be unavailable. */ }
    }
    document.querySelectorAll('[data-teams-calendar]').forEach(resetCalendar);
    refresh();
    if (activeModal) mountCalendar(activeModal.querySelector('[data-teams-calendar]'));
  };
  document.addEventListener('visitorConsentCollected', updateConsent);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', (event) => {
    if (activeModal && event.target.contains(activeModal)) closeModal();
    window.requestAnimationFrame(refresh);
  });

  window.TeamsDemoModal = { refresh };
  refresh();
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], updateConsent);
  }
})();
